/**
 * PHOTOS §4, taking: the `DeathCamera` the mission loop is handed, and who is handed one. The frame is copied at
 * once, synchronously, and the id handed back straight away; the JPEG is encoded and kept after, while the
 * memorial screens are already asking for it (`developing` lets them wait for it rather than miss it).
 */

import type { Deployment } from '../../meta/api';
import { cropFrame, newPhotoId } from './encode';
import type { PhotoStore } from './store';
import type { DeathCamera } from './types';

/**
 * Which deployments put a name on the Memorial Wall, and so get a camera. A table over the union, so a new mode
 * cannot be added without saying whether it takes pictures.
 */
export const PHOTO_MODES: Readonly<Record<Deployment['mode'], boolean>> = {
  operation: true,
  classic: true,
  practice: false,
  daily: false,
  training: false,
};

/** The camera for this deployment, or undefined: only an operation or Classic has a death worth a picture. */
export function cameraFor(mode: Deployment['mode'], camera: DeathCamera): DeathCamera | undefined {
  return PHOTO_MODES[mode] ? camera : undefined;
}

/** Pictures taken and not yet kept, by id: the port waits on these before it looks in the store. */
export type Developing = Map<string, Promise<void>>;

export interface CameraDeps {
  store: PhotoStore;
  /** RGBA, `PHOTO_W` x `PHOTO_H`, top row first -> a JPEG within `MAX_PHOTO_BYTES`, or null (`canvasEncode.ts`). */
  encode(rgba: Uint8ClampedArray): Promise<Blob | null>;
  randomBytes(count: number): Uint8Array;
  now(): number;
  developing: Developing;
  /** One was kept: the sync sends it. */
  kept(): void;
}

export function createDeathCamera(deps: CameraDeps): DeathCamera {
  const { store, developing } = deps;

  async function develop(id: string, frame: Uint8ClampedArray, at: number): Promise<void> {
    try {
      const jpeg = await deps.encode(frame);
      if (jpeg !== null && await store.keep({ id, jpeg, at, uploaded: false })) deps.kept();
    } catch {
      // A picture is an extra: one that could not be made is simply not there.
    }
  }

  return {
    take(pixels) {
      // Nowhere to keep it (no IndexedDB): photos are off, and the memorial entry carries no id.
      if (!store.usable()) return null;
      try {
        const frame = cropFrame(pixels());
        if (frame === null) return null;
        const id = newPhotoId(deps.randomBytes);
        const job = develop(id, frame, deps.now());
        developing.set(id, job);
        void job.then(() => developing.delete(id));
        return id;
      } catch {
        // No frame to read, or no entropy: no picture, and the mission hands over exactly as before.
        return null;
      }
    },
  };
}
